import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Toaster } from 'react-hot-toast';

import Header from 'Layout/Components/Header';
import Footer from 'Layout/Components/Footer';
import Loader from 'Components/Loader';
import BottomNavBar from 'Components/BottomNavBar/BottomNavBar';
import PullToRefresh from 'Components/PullToRefresh/PullToRefresh';
import SwipeNavigator from 'Components/SwipeNavigator/SwipeNavigator';
import OfflineIndicator from 'Components/OfflineIndicator/OfflineIndicator';
import EnvironmentBanner from 'Components/EnvironmentBanner/EnvironmentBanner';
import { RefreshProvider, useRefreshWithQuery } from 'Context/RefreshContext';
import { useIsMobile } from 'Hooks/useIsMobile';
import { getEnvironmentVariables } from 'Store/Actions/Environment/EnvironmentAction';

import '../styles/theme.css';
import '../styles/dark-theme.css';
import 'Layout/Layout.css';

/**
 * Inner layout component that consumes RefreshContext.
 * Separated so it can use useRefreshContext inside the provider.
 */
const LayoutContent = ({ children, style }) => {
  const dispatch = useDispatch();
  const { isMobile } = useIsMobile();
  const { refresh } = useRefreshWithQuery();
  const environment = useSelector((state) => state.environment?.environment);
  const showBanner = environment && environment !== 'prod';

  useEffect(() => {
    dispatch(getEnvironmentVariables());
  }, [dispatch]);

  useEffect(() => {
    if (showBanner) {
      document.body.classList.add('has-env-banner');
    } else {
      document.body.classList.remove('has-env-banner');
    }
    return () => document.body.classList.remove('has-env-banner');
  }, [showBanner]);

  const handleRefresh = useCallback(async () => {
    refresh();
  }, [refresh]);

  return (
    <div className="layout" style={style}>
      <EnvironmentBanner environment={environment} />
      <Loader />
      {!isMobile && <Header />}
      <OfflineIndicator />
      {isMobile ? (
        <PullToRefresh onRefresh={handleRefresh}>
          <SwipeNavigator>
            <main className="content content--mobile">{children}</main>
          </SwipeNavigator>
        </PullToRefresh>
      ) : (
        <main className="content">{children}</main>
      )}
      <Toaster
        position={isMobile ? 'top-center' : 'bottom-right'}
        toastOptions={{
          duration: 5000,
          style: {
            fontFamily: 'var(--font-family-base)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-md)',
            fontSize: 'var(--font-size-base)',
          },
          success: {
            style: { background: 'var(--color-success-light)', color: 'var(--color-success)' },
            iconTheme: { primary: 'var(--color-success)', secondary: 'var(--color-white)' },
          },
          error: {
            style: { background: 'var(--color-error-light)', color: 'var(--color-error)' },
            iconTheme: { primary: 'var(--color-error)', secondary: 'var(--color-white)' },
          },
        }}
        containerStyle={{
          zIndex: 10001,
          ...(isMobile ? { top: 60, bottom: 72 } : {}),
        }}
      />
      {isMobile && <BottomNavBar />}
      <Footer />
    </div>
  );
};

LayoutContent.propTypes = {
  children: PropTypes.node,
  style: PropTypes.object,
};

const Layout = ({ children, style }) => {
  return (
    <RefreshProvider>
      <LayoutContent style={style}>{children}</LayoutContent>
    </RefreshProvider>
  );
};

Layout.propTypes = {
  children: PropTypes.node,
  style: PropTypes.object,
};

export default Layout;
